import { useMemo } from 'react';
import { css } from '@emotion/react';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { useTranslation } from '@frontend/i18n';
import { Accordion, ChecklistField, DropdownField, Heading, Text } from '@frontend/design-system';
import { StatusBadge } from '../../../status-badge';
import { PreviewText } from '../../availability-form/atoms/preview-notification';
import {
  BookingCadenceOptions,
  bookingCadenceOptions,
  bookingChannelOptions,
  BookingChannelEnablement,
  GetAvailabilityRuleFormFieldProps,
} from '../../availability-form/types';
import {
  convertTimeToMinutes,
  getCadencePreviewDescriptionForRange,
  getSelectedServiceDuration,
} from '../../helpers/booking-rules-form-helpers';

type BookingChannelRulesFormProps = {
  locationId: string | undefined;
  getFieldProps: GetAvailabilityRuleFormFieldProps;
  bookingChannelEnablement: BookingChannelEnablement;
};

export const BookingChannelRulesForm = ({
  locationId,
  getFieldProps,
  bookingChannelEnablement,
}: BookingChannelRulesFormProps) => {
  const { t } = useTranslation('schedule-settings');
  const bookingCadenceFieldProps = getFieldProps('bookingCadence');
  const bookingChannelsFieldProps = getFieldProps('bookingChannels');
  const servicesFieldProps = getFieldProps('services');
  const startTime = getFieldProps('startTime') ?? '';
  const endTime = getFieldProps('endTime') ?? '';

  const enabledBookingChannelOptions = useMemo(
    () => bookingChannelOptions.filter(({ id }) => bookingChannelEnablement[id]),
    [bookingChannelEnablement]
  );

  const serviceTypesQuery = SchedulerV3.Helpers.useGetServiceTypesV3Data({
    selectedLocationId: locationId || '',
  });

  const selectedServiceDuration = useMemo(() => {
    return getSelectedServiceDuration({
      appointmentTypeGroups: serviceTypesQuery.data?.appointmentTypeGroups ?? [],
      selectedServiceIds: servicesFieldProps.value ?? [],
    });
  }, [serviceTypesQuery.data?.appointmentTypeGroups, servicesFieldProps]);

  const previewTextDescription = useMemo(() => {
    const startMinutes = convertTimeToMinutes(startTime?.value);
    const endMinutes = convertTimeToMinutes(endTime?.value);
    const cadenceId = bookingCadenceFieldProps.value as BookingCadenceOptions | undefined;

    if (
      !selectedServiceDuration ||
      !cadenceId ||
      startMinutes === null ||
      endMinutes === null ||
      endMinutes <= startMinutes
    ) {
      return null;
    }

    return getCadencePreviewDescriptionForRange({
      cadenceId,
      startMinutes,
      endMinutes,
      serviceDuration: selectedServiceDuration,
    });
  }, [bookingCadenceFieldProps.value, startTime, endTime, selectedServiceDuration]);

  const headerSubtitle = useMemo(() => {
    const cadenceLabel = bookingCadenceOptions.find((o) => o.id === bookingCadenceFieldProps.value)?.displayName;

    const selectedChannels = enabledBookingChannelOptions.filter((option) =>
      bookingChannelsFieldProps.value?.includes(option.id)
    );

    return (
      <div css={subHeaderStyles}>
        <Text>{cadenceLabel}</Text>
        {selectedChannels.length > 0 && <div css={separatorStyles} />}
        {selectedChannels.map(({ id }) => (
          <StatusBadge key={id} variant={id} />
        ))}
      </div>
    );
  }, [enabledBookingChannelOptions, bookingCadenceFieldProps.value, bookingChannelsFieldProps.value]);

  return (
    <Accordion.Item value='booking-rules'>
      <Accordion.Header>
        <div css={accordionHeaderStyles}>
          <Heading level={3}>{t('Booking Rules')}</Heading>
          {headerSubtitle}
        </div>
      </Accordion.Header>
      <Accordion.Body>
        {enabledBookingChannelOptions.length > 0 && (
          <>
            <Text weight='bold'>{t('Who can book this')}</Text>
            <Text>
              {t(
                'Choose which channels can see this availability. What each channel does with it depends on its current scheduling mode. Your staff can always book an appointment where they need to.'
              )}
            </Text>
            <ChecklistField {...bookingChannelsFieldProps} label='' showLabel={false} css={bookingChannelsFieldStyles}>
              {enabledBookingChannelOptions.map((item) => (
                <ChecklistField.Option key={item.id} name={item.id} css={bookingChannelOptionStyles}>
                  <Text size='medium' weight='bold'>
                    {item.title}
                  </Text>
                  <Text size='small' color='subdued'>
                    {item.description}
                  </Text>
                </ChecklistField.Option>
              ))}
            </ChecklistField>
          </>
        )}
        <>
          <DropdownField label={t('Booking Cadence')} {...bookingCadenceFieldProps} hasNonFloatingLabel>
            {bookingCadenceOptions?.map((item) => (
              <DropdownField.Option key={item.id} value={item.id}>
                {item.displayName}
              </DropdownField.Option>
            ))}
          </DropdownField>
          {previewTextDescription && <PreviewText title={t('Cadence preview:')} description={previewTextDescription} />}
        </>
      </Accordion.Body>
    </Accordion.Item>
  );
};

const accordionHeaderStyles = css`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const subHeaderStyles = css`
  display: flex;
  align-items: center;
  gap: var(--fabric-space-gap-sm);
`;

const separatorStyles = css`
  width: 1px;
  height: 100%;
  background-color: var(--fabric-color-neutral-alpha-100-alpha-75);
`;

const bookingChannelsFieldStyles = css`
  display: flex;
  flex-direction: column;
  gap: var(--fabric-space-gap-md);
  margin-top: var(--fabric-space-gap-sm);
  margin-bottom: var(--fabric-space-gap-lg);
`;

const bookingChannelOptionStyles = css`
  padding: var(--fabric-space-gap-md);
  margin-bottom: 0;
  border: 1px solid var(--fabric-color-border-default);
  border-radius: var(--border-radius-medium);

  &:has(input:checked) {
    border-color: var(--fabric-color-accent-blue-base);
  }

  &:hover {
    background-color: var(--fabric-color-neutral-5);
  }
`;
